/**
 * @fileoverview Color Picker Popover
 *
 * Purpose: Inline color picker that lets users pick a color visually,
 * edit the hex value directly, or edit the R/G/B channels individually.
 * Replaces the native <input type="color"> picker, which on macOS only
 * exposes RGB sliders without a hex field.
 *
 * Used by: Branding settings editor preferences (preset color palette).
 */

import { useEffect, useState } from "react";
import { HexColorPicker, HexColorInput } from "react-colorful";
import { cn } from "@/utils/misc";

interface ColorPickerPopoverProps {
  /** Current hex color value (e.g. "#1a56db"). Always 6-digit hex. */
  value: string;
  /** Called whenever the color changes (live, every drag/keystroke). */
  onChange: (color: string) => void;
  className?: string;
}

/**
 * Convert a hex string ("#rrggbb") to its RGB channel components.
 * Returns null when the hex is not a valid 6-digit string.
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) return null;
  const num = parseInt(match[1], 16);
  return {
    r: (num >> 16) & 0xff,
    g: (num >> 8) & 0xff,
    b: num & 0xff,
  };
}

/** Clamp a number to 0–255 and convert to a 2-digit hex string. */
function channelToHex(value: number): string {
  const clamped = Math.max(0, Math.min(255, Math.round(value)));
  return clamped.toString(16).padStart(2, "0");
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}`;
}

/**
 * ColorPickerPopover — visual swatch + hex input + RGB inputs.
 *
 * The component keeps a local mirror of the hex value so users can type
 * partial hex strings (e.g. "#1a") without the parent state thrashing,
 * but the canonical state still lives with the parent via `onChange`.
 */
export function ColorPickerPopover({
  value,
  onChange,
  className,
}: ColorPickerPopoverProps) {
  // Local mirror of channels lets users edit each input freely without
  // losing focus when intermediate values (e.g. empty string) are typed.
  const initialRgb = hexToRgb(value) ?? { r: 0, g: 0, b: 0 };
  const [rText, setRText] = useState(String(initialRgb.r));
  const [gText, setGText] = useState(String(initialRgb.g));
  const [bText, setBText] = useState(String(initialRgb.b));

  useEffect(() => {
    const rgb = hexToRgb(value);
    if (!rgb) return;
    setRText(String(rgb.r));
    setGText(String(rgb.g));
    setBText(String(rgb.b));
  }, [value]);

  const commitChannel = (channel: "r" | "g" | "b", raw: string) => {
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    const current = hexToRgb(value) ?? { r: 0, g: 0, b: 0 };
    const next = { ...current, [channel]: parsed };
    onChange(rgbToHex(next.r, next.g, next.b));
  };

  return (
    <div
      className={cn(
        "w-56 rounded-lg border border-border bg-card p-3 shadow-lg",
        className,
      )}
    >
      <HexColorPicker
        color={value}
        onChange={onChange}
        style={{ width: "100%", height: 160 }}
      />

      <div className="mt-3 flex items-center gap-2">
        <label className="text-[10px] font-medium uppercase text-primary/40">
          Hex
        </label>
        <HexColorInput
          color={value}
          onChange={onChange}
          prefixed
          className="h-7 min-w-0 flex-1 rounded border border-border bg-background px-2 font-mono text-xs uppercase focus:outline-none focus:ring-1 focus:ring-primary/30"
        />
      </div>

      <div className="mt-2 grid grid-cols-3 gap-2">
        {[
          { label: "R", channel: "r" as const, value: rText, set: setRText },
          { label: "G", channel: "g" as const, value: gText, set: setGText },
          { label: "B", channel: "b" as const, value: bText, set: setBText },
        ].map(({ label, channel, value: chValue, set }) => (
          <div key={label} className="flex flex-col items-center">
            <label className="text-[10px] font-medium uppercase text-primary/40">
              {label}
            </label>
            <input
              type="number"
              min={0}
              max={255}
              value={chValue}
              onChange={(e) => {
                set(e.target.value);
                if (e.target.value !== "")
                  commitChannel(channel, e.target.value);
              }}
              onBlur={(e) => {
                if (e.target.value === "") {
                  const rgb = hexToRgb(value) ?? { r: 0, g: 0, b: 0 };
                  set(String(rgb[channel]));
                }
              }}
              className="h-7 w-full rounded border border-border bg-background px-1 text-center font-mono text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
